import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import Tree from 'react-d3-tree';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Loader2, Users, MapPin } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Node renderer styled like the user-attached reference: rounded card with
 * bold name and a small subtitle (bairro/cidade). Green for leaders, blue for
 * supporters. Cards are clickable when onNodeClick is provided.
 */
const NodeCard = ({ nodeDatum, onNodeClick }) => {
  const isDobrada = nodeDatum.kind === 'dobrada';
  const isLeader = nodeDatum.kind === 'leader';
  const stroke = isDobrada ? '#7c3aed' : isLeader ? '#10b981' : '#3b82f6';
  const bg = isDobrada ? '#f5f3ff' : isLeader ? '#ecfdf5' : '#eff6ff';
  const text = isDobrada ? '#5b21b6' : isLeader ? '#065f46' : '#1e3a8a';

  return (
    <g
      style={{ cursor: onNodeClick ? 'pointer' : 'default' }}
      onClick={() => onNodeClick && onNodeClick(nodeDatum)}
      data-testid={`genealogy-node-${nodeDatum.id || nodeDatum.name}`}
    >
      <rect
        x={-110}
        y={-26}
        width={220}
        height={52}
        rx={8}
        ry={8}
        fill="white"
        stroke={stroke}
        strokeWidth={1.5}
      />
      <circle cx={-92} cy={0} r={6} fill={stroke} />
      <foreignObject x={-78} y={-22} width={186} height={44}>
        <div
          style={{
            fontFamily:
              'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            color: text,
            background: bg,
            padding: '4px 8px',
            borderRadius: 4,
          }}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: 12,
              lineHeight: 1.15,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {nodeDatum.name}
          </div>
          {nodeDatum.subtitle && (
            <div
              style={{
                fontSize: 10,
                lineHeight: 1.1,
                opacity: 0.75,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {nodeDatum.subtitle}
            </div>
          )}
        </div>
      </foreignObject>
    </g>
  );
};

/**
 * Leaflet map showing pins for the network (green = leader, blue = supporter).
 */
const NetworkMap = ({ points = [], height = 420 }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      zoomControl: true,
      scrollWheelZoom: true,
    }).setView([-14.235, -51.9253], 4);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
    }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }
    if (!points.length) return;
    const group = L.layerGroup();
    points.forEach((p) => {
      const color = p.kind === 'leader' ? '#10b981' : '#3b82f6';
      const marker = L.circleMarker([p.lat, p.lng], {
        radius: p.kind === 'leader' ? 8 : 6,
        fillColor: color,
        color: '#fff',
        weight: 2,
        opacity: 1,
        fillOpacity: 0.85,
      });
      marker.bindPopup(
        `<div style="font-family: sans-serif; font-size: 12px;">
          <div style="font-weight: 700; color: ${color}; text-transform: uppercase; font-size: 10px;">
            ${p.kind === 'leader' ? 'Liderança' : 'Apoiador'}
          </div>
          <div style="font-weight: 600; margin-top: 2px;">${p.name || '—'}</div>
          ${p.bairro ? `<div style="color: #64748b; font-size: 11px;">${p.bairro}</div>` : ''}
        </div>`
      );
      group.addLayer(marker);
    });
    group.addTo(map);
    layerRef.current = group;
    // Fit bounds
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [points]);

  return (
    <div className="relative">
      <div
        ref={containerRef}
        style={{ height, width: '100%' }}
        className="rounded-lg overflow-hidden border border-slate-200"
        data-testid="genealogy-map"
      />
      <div className="flex items-center gap-3 mt-2 text-xs text-slate-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-emerald-500" /> Lideranças
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-blue-500" /> Apoiadores
        </span>
      </div>
    </div>
  );
};

/**
 * LeaderGenealogy — full panel: tabs (Árvore / Mapa) over a single fetch.
 * Can also be used for dobradas by passing `endpoint` prop pointing to
 * `/api/dobradas/{id}/genealogy` (same response shape).
 */
export const LeaderGenealogy = ({ leaderId, endpoint, onNodeClick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('tree');

  const fetchGenealogy = useCallback(async () => {
    const url = endpoint || (leaderId ? `${BACKEND_URL}/api/leaders/${leaderId}/genealogy` : null);
    if (!url) return;
    setLoading(true);
    setError('');
    try {
      const { data: resp } = await axios.get(url, { withCredentials: true });
      setData(resp);
    } catch (_) {
      setError('Erro ao carregar a árvore');
    } finally {
      setLoading(false);
    }
  }, [leaderId, endpoint]);

  useEffect(() => {
    fetchGenealogy();
  }, [fetchGenealogy]);

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Carregando rede…
      </div>
    );
  }
  if (error) {
    return <div className="py-8 text-center text-sm text-rose-600">{error}</div>;
  }
  if (!data || !data.tree) {
    return <div className="py-8 text-center text-sm text-slate-500">Nenhum dado disponível.</div>;
  }

  const directChildren = data.tree.children?.length || 0;
  const totalDescendants = (() => {
    let n = 0;
    const walk = (node) => {
      if (!node?.children) return;
      n += node.children.length;
      node.children.forEach(walk);
    };
    walk(data.tree);
    return n;
  })();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          <strong className="text-slate-900">{totalDescendants}</strong> contato(s) na rede
          {' '}· <strong className="text-slate-900">{directChildren}</strong> direto(s)
        </div>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="tree" className="text-xs gap-1.5" data-testid="genealogy-tab-tree">
            <Users className="h-3.5 w-3.5" /> Árvore
          </TabsTrigger>
          <TabsTrigger value="map" className="text-xs gap-1.5" data-testid="genealogy-tab-map">
            <MapPin className="h-3.5 w-3.5" /> Mapa
          </TabsTrigger>
        </TabsList>
        <TabsContent value="tree" className="mt-3">
          <div
            className="rounded-lg border border-slate-200 bg-slate-50/40 overflow-hidden"
            style={{ height: 460 }}
          >
            {totalDescendants === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-500 px-6 text-center">
                Esta liderança ainda não convidou outras lideranças ou apoiadores.
              </div>
            ) : (
              <Tree
                data={data.tree}
                orientation="horizontal"
                pathFunc="step"
                translate={{ x: 140, y: 230 }}
                separation={{ siblings: 0.7, nonSiblings: 0.9 }}
                nodeSize={{ x: 260, y: 70 }}
                zoom={0.7}
                collapsible={false}
                renderCustomNodeElement={(props) => (
                  <NodeCard {...props} onNodeClick={onNodeClick} />
                )}
              />
            )}
          </div>
        </TabsContent>
        <TabsContent value="map" className="mt-3">
          {(!data.map_points || data.map_points.length === 0) ? (
            <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 py-12 text-center text-sm text-slate-500 px-6">
              Sem coordenadas para mostrar no mapa.
              <div className="text-xs text-slate-400 mt-2">
                Lideranças e apoiadores que permitirem geolocalização aparecerão aqui.
              </div>
            </div>
          ) : (
            <NetworkMap points={data.map_points} height={420} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LeaderGenealogy;
